import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initializeAnalytics, trackEvent, trackPageView } from "../utils/analytics";

const getPageType = (pathname: string) => {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/places/city/")) return "city";
  if (pathname.startsWith("/places/category/")) return "category";
  if (pathname.startsWith("/places/")) return "place";
  if (pathname.startsWith("/planner")) return "planner";
  if (pathname.startsWith("/search-results")) return "search_results";
  if (pathname.startsWith("/userProfile")) return "profile";
  return "other";
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    initializeAnalytics();
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    if (previousPath.current === path) return;

    previousPath.current = path;
    trackPageView(path);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    let hasTracked = false;
    let animationFrame: number | null = null;

    const trackScrollDepth = () => {
      animationFrame = null;
      if (hasTracked) return;

      const documentHeight = document.documentElement.scrollHeight;
      if (documentHeight <= 0) return;

      const scrollDepth = (window.scrollY + window.innerHeight) / documentHeight;
      if (scrollDepth < 0.75) return;

      hasTracked = true;
      trackEvent("scroll_75_percent", {
        page_path: path,
        page_type: getPageType(location.pathname),
      });
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };

    const handleScroll = () => {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(trackScrollDepth);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
